import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface BarcodeScannerAnimationProps {
  onScanComplete: (orderNumber: string) => void;
  isActive: boolean;
}

export const BarcodeScannerAnimation = ({ onScanComplete, isActive }: BarcodeScannerAnimationProps) => {
  const [scannedValue, setScannedValue] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [displayNumber, setDisplayNumber] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const bufferRef = useRef<string>("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isActive]);

  // Handle barcode scanner input - use onChange only to prevent duplicate characters
  // Barcode scanners trigger both keydown and input events, causing duplication
  // NOTE: On some Android WebViews / scanners there can be a small delay between digits,
  // so we wait a bit longer and only auto-complete once we have at least 4 digits.
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const value = e.target.value.replace(/\D/g, "");
    bufferRef.current = value;
    setScannedValue(value);

    // Complete scan after a short idle period (some scanners have inter-digit delays)
    timeoutRef.current = setTimeout(() => {
      const buffered = bufferRef.current;
      if (buffered.length >= 4) {
        completeScan(buffered);
      }
    }, 350);
  };

  // Handle Enter key separately
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && bufferRef.current.length > 0) {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      completeScan(bufferRef.current);
    }
  };

  // Play success sound using Web Audio API
  const playSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create a pleasant two-tone success sound
      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + startTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + startTime + duration);
        
        oscillator.start(audioContext.currentTime + startTime);
        oscillator.stop(audioContext.currentTime + startTime + duration);
      };
      
      // Two ascending tones for a pleasant "success" sound
      playTone(523.25, 0, 0.15); // C5
      playTone(659.25, 0.1, 0.2); // E5
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const completeScan = (value: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setDisplayNumber(value);
    setShowSuccess(true);
    
    // Play success sound
    playSuccessSound();
    
    // After success animation, callback with the value
    setTimeout(() => {
      onScanComplete(value);
      // Reset for next scan
      bufferRef.current = "";
      setScannedValue("");
      setShowSuccess(false);
      setDisplayNumber("");
    }, 1500);
  };

  if (!isActive) return null;

  return (
    <div className="flex flex-col items-center justify-center py-8">
      {/* Hidden input for capturing barcode scanner input */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="fixed top-0 left-0 w-1 h-1 opacity-0 pointer-events-auto z-50"
        style={{ fontSize: '16px' }}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        value={scannedValue}
        autoFocus
      />

      {showSuccess ? (
        // Success Animation
        <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
          <div className="success-checkmark">
            <div className="check-icon">
              <span className="icon-line line-tip"></span>
              <span className="icon-line line-long"></span>
              <div className="icon-circle"></div>
              <div className="icon-fix"></div>
            </div>
          </div>
          <div className="text-2xl font-bold text-primary animate-in zoom-in duration-300">
            #{displayNumber}
          </div>
        </div>
      ) : (
        // Scanner Animation
        <div 
          className="ocrloader cursor-pointer" 
          onClick={() => inputRef.current?.focus()}
          role="button"
          tabIndex={0}
        >
          <em></em>
          <div>
            <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
            <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
            <i></i><i></i><i></i>
          </div>
          <span></span>
        </div>
      )}

      <p className={cn(
        "mt-6 text-sm text-muted-foreground text-center transition-opacity",
        showSuccess ? "opacity-0" : "opacity-100"
      )}>
        {scannedValue ? (
          <span className="font-mono text-lg text-foreground">{scannedValue}</span>
        ) : (
          "סרוק ברקוד מהמעטפה..."
        )}
      </p>

      {/* Manual entry option */}
      {!showSuccess && (
        <button
          type="button"
          className="mt-4 text-xs text-muted-foreground hover:text-primary underline transition-colors"
          onClick={() => {
            const manualValue = prompt("הזן מספר הזמנה ידנית:");
            if (manualValue && /^\d+$/.test(manualValue)) {
              completeScan(manualValue);
            }
          }}
        >
          הזנה ידנית
        </button>
      )}

      <style>{`
        .ocrloader {
          width: 94px;
          height: 77px;
          position: relative;
          backface-visibility: hidden;
        }
        
        .ocrloader span {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 20px;
          background-color: hsl(var(--primary) / 0.5);
          z-index: 1;
          transform: translateY(135%);
          animation: barcode-move 0.7s cubic-bezier(0.15, 0.44, 0.76, 0.64) infinite;
        }
        
        .ocrloader > div {
          z-index: 1;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 48%;
          backface-visibility: hidden;
        }
        
        .ocrloader i {
          display: block;
          height: 1px;
          background: hsl(var(--foreground));
          margin: 0 auto 2.2px;
          backface-visibility: hidden;
        }
        
        .ocrloader i:nth-child(2) { width: 75%; }
        .ocrloader i:nth-child(3) { width: 81%; }
        .ocrloader i:nth-child(4) { width: 87%; }
        .ocrloader i:nth-child(6) { width: 71%; }
        .ocrloader i:nth-child(7) { width: 81%; }
        .ocrloader i:nth-child(8) { width: 65%; }
        .ocrloader i:nth-child(9) { width: 83%; }
        .ocrloader i:nth-child(10) { width: 75%; }
        .ocrloader i:nth-child(12) { width: 86%; }
        .ocrloader i:nth-child(14) { width: 65%; }
        .ocrloader i:nth-child(16) { width: 75%; }
        .ocrloader i:nth-child(18) { width: 83%; }
        
        .ocrloader:before,
        .ocrloader:after,
        .ocrloader em:after,
        .ocrloader em:before {
          border-color: hsl(var(--foreground));
          content: "";
          position: absolute;
          width: 19px;
          height: 16px;
          border-style: solid;
          border-width: 0px;
        }
        
        .ocrloader:before {
          left: 0;
          top: 0;
          border-left-width: 2px;
          border-top-width: 2px;
        }
        
        .ocrloader:after {
          right: 0;
          top: 0;
          border-right-width: 2px;
          border-top-width: 2px;
        }
        
        .ocrloader em:before {
          left: 0;
          bottom: 0;
          border-left-width: 2px;
          border-bottom-width: 2px;
        }
        
        .ocrloader em:after {
          right: 0;
          bottom: 0;
          border-right-width: 2px;
          border-bottom-width: 2px;
        }
        
        @keyframes barcode-move {
          0%, 100% { transform: translateY(135%); }
          50% { transform: translateY(0%); }
          75% { transform: translateY(272%); }
        }

        /* Success Checkmark Animation */
        .success-checkmark {
          width: 80px;
          height: 80px;
          margin: 0 auto;
        }
        
        .check-icon {
          width: 80px;
          height: 80px;
          position: relative;
          border-radius: 50%;
          box-sizing: content-box;
          border: 4px solid hsl(var(--primary));
        }
        
        .check-icon::before {
          top: 3px;
          left: -2px;
          width: 30px;
          transform-origin: 100% 50%;
          border-radius: 100px 0 0 100px;
          content: '';
          height: 100px;
          position: absolute;
          background: hsl(var(--background));
          transform: rotate(-45deg);
        }
        
        .check-icon::after {
          top: 0;
          left: 30px;
          width: 60px;
          transform-origin: 0 50%;
          border-radius: 0 100px 100px 0;
          animation: rotate-circle 4.25s ease-in;
          content: '';
          height: 100px;
          position: absolute;
          background: hsl(var(--background));
          transform: rotate(-45deg);
        }
        
        .check-icon .icon-line {
          height: 5px;
          background-color: hsl(var(--primary));
          display: block;
          border-radius: 2px;
          position: absolute;
          z-index: 10;
        }
        
        .check-icon .icon-line.line-tip {
          top: 46px;
          left: 14px;
          width: 25px;
          transform: rotate(45deg);
          animation: icon-line-tip 0.75s;
        }
        
        .check-icon .icon-line.line-long {
          top: 38px;
          right: 8px;
          width: 47px;
          transform: rotate(-45deg);
          animation: icon-line-long 0.75s;
        }
        
        .check-icon .icon-circle {
          top: -4px;
          left: -4px;
          z-index: 10;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          position: absolute;
          box-sizing: content-box;
          border: 4px solid hsl(var(--primary) / 0.5);
        }
        
        .check-icon .icon-fix {
          top: 8px;
          width: 5px;
          left: 26px;
          z-index: 1;
          height: 85px;
          position: absolute;
          transform: rotate(-45deg);
          background-color: hsl(var(--background));
        }
        
        @keyframes rotate-circle {
          0% { transform: rotate(-45deg); }
          5% { transform: rotate(-45deg); }
          12% { transform: rotate(-405deg); }
          100% { transform: rotate(-405deg); }
        }
        
        @keyframes icon-line-tip {
          0% { width: 0; left: 1px; top: 19px; }
          54% { width: 0; left: 1px; top: 19px; }
          70% { width: 50px; left: -8px; top: 37px; }
          84% { width: 17px; left: 21px; top: 48px; }
          100% { width: 25px; left: 14px; top: 45px; }
        }
        
        @keyframes icon-line-long {
          0% { width: 0; right: 46px; top: 54px; }
          65% { width: 0; right: 46px; top: 54px; }
          84% { width: 55px; right: 0px; top: 35px; }
          100% { width: 47px; right: 8px; top: 38px; }
        }
      `}</style>
    </div>
  );
};
